import { Injectable } from '@angular/core';
import { ConfigService } from 'src/app/service/config.service';
import { RoutingService } from 'src/app/service/routing.service';
import { QueryParamsService } from './query-params.service';
import { AuthService } from 'src/app/service/auth.service';
import { PortfolioApiService } from '../services/customer-api/portfolio-api.service';
import { QueryParams } from '../models/query-params.interface';
import { DateUtils } from './date.utils';
import moment from 'moment';
import { EventFilter, TimeRange } from '../models/news.interface';
import {
  EventTemporality,
  EventTemporalityWire,
} from '../models/events.interface';
import { TagUtils } from './tag.utils';
import { FACTUALITY_VALUE_MAP } from './params-builder';

export interface BaseContentPayloadParams {
  type: 'search_query' | 'entities';
  timePeriod?: string;
}

/**
 * Inputs accepted by `generateTimeRange`. Override precedence:
 *   1. Explicit `dateFrom` + `dateTo` (both required)
 *   2. Explicit `time` (URL-style string, e.g. '7' or '2026-01-01--2026-02-01')
 *   3. Explicit `newsday` — narrows to `[newsday, newsday + 1d]`
 *   4. URL `newsday` query param (caller cannot suppress at this layer)
 *   5. Reader/storyReader context (90 days)
 *   6. `timePeriod` override (legacy field, kept for callers in stats/news flows)
 *   7. URL `time` query param
 *   8. Hard default of 7 days
 */
export interface TimeRangeInput extends Partial<BaseContentPayloadParams> {
  time?: string;
  dateFrom?: string;
  dateTo?: string;
  newsday?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CommonDataParamsService {
  project: string = '';
  currentQueryParams: QueryParams = {} as QueryParams;

  constructor(
    public config: ConfigService,
    public routing: RoutingService,
    private queryParamsService: QueryParamsService,
    protected auth: AuthService,
    protected portfolioApi: PortfolioApiService,
  ) {
    this.project = this.config.appConfig.routing.apiproj;
    this.queryParamsService.queryParams$.subscribe((params) => {
      this.currentQueryParams = params;
    });
  }

  // ========================================================================================
  // ENTITY MANAGEMENT METHODS
  // ========================================================================================

  public getEntities(
    queryParams: QueryParams,
    type: string,
  ): { entities: string[] }[] {
    if (type === 'search_query') {
      return this.getSearchQueryEntities(queryParams);
    }
    if (type === 'entities') {
      return this.getEntityBasedEntities(queryParams);
    }
    return [];
  }

  private getSearchQueryEntities(
    queryParams: QueryParams,
  ): { entities: string[] }[] {
    const result: { entities: string[] }[] = [];

    const parsedTag = TagUtils.parse(queryParams.tag);
    if (parsedTag) {
      result.push({ entities: [parsedTag.key] });
    }

    if (queryParams.participant) {
      const [tagType, tagId] = queryParams.participant.split(':');
      result.push({ entities: [`${tagType}:${tagId}`] });
    }

    return result;
  }

  private getEntityBasedEntities(
    queryParams: QueryParams,
  ): { entities: string[] }[] {
    const result: { entities: string[] }[] = [];
    const baseEntities = this.getBaseEntities(queryParams);

    if (baseEntities.length > 0) {
      result.push({ entities: baseEntities });
    }

    // Add tag entities for non-scanner case
    const tagEntities = this.getTagEntities(queryParams);
    if (tagEntities.length > 0) {
      result.push({ entities: tagEntities });
    }

    return result;
  }

  private getBaseEntities(queryParams: QueryParams): string[] {
    // Market portfolio takes priority
    if (this.portfolioApi.activePortfolio?.marketPortfolio) {
      return [...this.portfolioApi.activePortfolioContent];
    }

    // Direct type and id
    if (queryParams.type && queryParams.id) {
      return [`${queryParams.type}:${queryParams.id}`];
    }

    // Scanner with tag case
    if (this.isScannerTagCase(queryParams)) {
      const parsedTag = TagUtils.parse(queryParams.tag);
      return parsedTag ? [parsedTag.key] : [];
    }

    // Default folio content
    return [...this.portfolioApi.activePortfolioContent];
  }

  private getTagEntities(queryParams: QueryParams): string[] {
    if (!this.routing.isScanner() && queryParams.tag) {
      const parsedTag = TagUtils.parse(queryParams.tag);
      return parsedTag ? [parsedTag.key] : [];
    }
    return [];
  }

  private isScannerTagCase(queryParams: QueryParams): boolean {
    return (
      this.routing.isScanner() &&
      !!queryParams.tag &&
      !queryParams.type &&
      !queryParams.scoreForTabs &&
      !queryParams.itemized
    );
  }

  // ========================================================================================
  // EVENT FILTER METHODS
  // ========================================================================================

  public getFactualityValues(factualityParam: string): string[] {
    const result: string[] = [];
    for (const [key, expanded] of Object.entries(FACTUALITY_VALUE_MAP)) {
      if (factualityParam.includes(key)) {
        result.push(...expanded);
      }
    }
    return result;
  }

  /**
   * Creates event filter based on query parameters and entities
   * If event_ids or eventId exists, entities are moved to event_participants
   */
  public createEventFilter(
    queryParams: QueryParams,
    entities: string[],
  ): EventFilter | undefined {
    const eventFilter: EventFilter = {};

    const eventIds = this.getEventIds(queryParams);
    if (eventIds) {
      eventFilter.event_ids = eventIds;
    }

    // When event params exist, move entities to event_participants
    if (entities.length > 0) {
      eventFilter.event_participants = entities;
    }

    if (queryParams.temporality) {
      eventFilter.temporality = queryParams.temporality.split(',');
    }

    if (queryParams.factuality) {
      eventFilter.factuality = this.getFactualityValues(queryParams.factuality);
    }

    return eventFilter;
  }

  /**
   * Resolves the event participant list (prefixed `type:id`) used by both
   * `score_ts` (`event_filter.event_participants`) and the v5 events POST.
   *
   * Single source of truth; do not duplicate this logic in callers.
   */
  public getEventParticipants(
    params: { entity?: string[]; entityType?: string } = {},
    queryParams: QueryParams = this.currentQueryParams,
  ): string[] {
    if (params.entity?.length && params.entityType) {
      return params.entity.map((e) => `${params.entityType}:${e}`);
    }
    if (params.entity?.length) {
      return params.entity;
    }
    const entities = this.getEntities(queryParams, 'entities');
    return entities[0]?.entities ?? [];
  }

  /**
   * Normalizes a URL-style temporality value (`past,present`) to the v5
   * wire format (`PAST`, `PRESENT`, ...). When `explicit` is supplied it
   * takes precedence over the CSV (still uppercased to wire format).
   */
  public getTemporalityWire(
    temporalityCsv?: string,
    explicit?: EventTemporality[],
  ): EventTemporalityWire[] | undefined {
    if (explicit?.length) {
      return explicit.map((t) => t.toUpperCase() as EventTemporalityWire);
    }
    if (!temporalityCsv) {
      return undefined;
    }
    return temporalityCsv
      .split(',')
      .map((t) => t.trim().toUpperCase()) as EventTemporalityWire[];
  }

  /**
   * Resolves event ids from explicit value or URL query params
   * (`eventid` wins over `event_ids`). Shared by `score_ts`
   * (`ScoreUtilsService`) and the v5 events POST (`EventsUtilsService`).
   */
  public getEventIds(
    queryParams: QueryParams = this.currentQueryParams,
    explicit?: string[],
  ): string[] | undefined {
    if (explicit?.length) {
      return explicit;
    }
    if (queryParams.eventid) {
      return [queryParams.eventid];
    }
    if (queryParams.event_ids) {
      return queryParams.event_ids.split(',');
    }
    return undefined;
  }

  public hasEventParameters(queryParams: QueryParams): boolean {
    const eventParameters = [
      'event_ids',
      'eventid',
      'temporality',
      'factuality',
    ];
    return eventParameters.some((param) => queryParams[param]);
  }

  // ========================================================================================
  // TIME RANGE METHODS
  // ========================================================================================

  public generateTimeRange(input?: TimeRangeInput): TimeRange {
    if (input?.dateFrom && input?.dateTo) {
      return { date_from: input.dateFrom, date_to: input.dateTo };
    }

    if (input?.time) {
      const r = DateUtils.fromTo(input.time);
      return { date_from: r.dateFrom, date_to: r.dateTo };
    }

    if (input?.newsday) {
      return this.newsdayRange(input.newsday);
    }

    const queryParams = this.currentQueryParams;

    if (queryParams.newsday) {
      return this.newsdayRange(queryParams.newsday);
    }

    const timePeriod = DateUtils.getTimePeriod(
      this.routing.isReader() || this.routing.isStoryReader(),
      input,
      queryParams,
    );

    const timeRange = DateUtils.fromTo(timePeriod);

    return {
      date_from: timeRange.dateFrom,
      date_to: timeRange.dateTo,
    };
  }

  private newsdayRange(newsday: string): TimeRange {
    return {
      date_from: moment(newsday).format('YYYY-MM-DD'),
      date_to: moment(newsday).add(1, 'days').format('YYYY-MM-DD'),
    };
  }

  // ========================================================================================
  // COMMON PAYLOAD METHODS
  // ========================================================================================

  public getProject(): string {
    return this.project;
  }

  public getCurrentQueryParams(): QueryParams {
    return this.currentQueryParams;
  }
}
